// src/pages/app/TravelRequestsPage.jsx — Travel request list & management
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { travelRequestsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, Eye, CheckCircle2, XCircle, Upload, Filter, Plane } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  booked: 'bg-indigo-100 text-indigo-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'booked', label: 'Booked' },
  { value: 'rejected', label: 'Rejected' },
];

export default function TravelRequestsPage() {
  const { isRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [actionLoading, setActionLoading] = useState({});

  const [rejectModal, setRejectModal] = useState({ open: false, id: null, comment: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await travelRequestsApi.list({ status: statusFilter || undefined });
      setRequests(data);
    } catch { toast.error('Failed to load travel requests'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleApprove = async (id) => {
    setActionLoading(p => ({ ...p, [id]: 'approving' }));
    try {
      await travelRequestsApi.approve(id, { comment: 'Approved' });
      toast.success('Travel request approved!');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setActionLoading(p => ({ ...p, [id]: null })); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal.id || !rejectModal.comment.trim() || rejectModal.comment.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    const id = rejectModal.id;
    setActionLoading(p => ({ ...p, [id]: 'rejecting' }));
    try {
      await travelRequestsApi.reject(id, { comment: rejectModal.comment });
      toast.success('Travel request rejected');
      setRejectModal({ open: false, id: null, comment: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rejection failed'); }
    finally { setActionLoading(p => ({ ...p, [id]: null })); }
  };

  const filtered = requests.filter(r =>
    (r.destination?.toLowerCase().includes(search.toLowerCase()) ||
     r.purpose?.toLowerCase().includes(search.toLowerCase()) ||
     r.employee_name?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">Travel Requests</h1>
          <p className="text-slate-500 text-sm mt-1">Manage pre-travel booking requests and approvals</p>
        </div>
        {isRole('employee', 'admin') && (
          <Link to="/app/travel-requests/new"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" /> New Request
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by destination or purpose..."
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <Select
          value={statusFilter || 'all'}
          onValueChange={val => {
            const finalVal = val === 'all' ? '' : val;
            setStatusFilter(finalVal);
            setSearchParams(finalVal ? { status: finalVal } : {});
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(o => (
              <SelectItem key={o.value || 'all'} value={o.value || 'all'}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-slate-200 bg-white overflow-hidden shadow-sm">
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs text-slate-500 font-medium">
          <span>Showing <strong>{filtered.length}</strong> travel request{filtered.length === 1 ? '' : 's'}</span>
        </div>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : !filtered.length ? (
            <div className="text-center py-16">
              <p className="text-slate-400 text-sm">No travel requests found</p>
              {isRole('employee', 'admin') && (
                <Link to="/app/travel-requests/new" className="mt-3 inline-block text-blue-600 text-sm font-semibold hover:underline">
                  Create your first request →
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Destination & Purpose</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Employee</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Travel Dates</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Budget Est.</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Status</th>
                    <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(req => (
                    <tr key={req.id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                            <Plane className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="font-semibold text-slate-900 block group-hover:text-blue-600 transition-colors">
                              {req.origin ? `${req.origin} ➔ ${req.destination}` : req.destination}
                            </span>
                            <span className="text-xs text-slate-500 truncate max-w-[220px] block">{req.purpose || 'Business Travel'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center text-[10px]">
                            {req.employee_name?.charAt(0) || 'E'}
                          </div>
                          <span className="text-xs font-medium text-slate-700">{req.employee_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium whitespace-nowrap">
                        {new Date(req.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} –{' '}
                        {new Date(req.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">
                        {req.budget_estimate ? `₹${parseFloat(req.budget_estimate).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 whitespace-nowrap">
                        <span className={cn("inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border capitalize", STATUS_COLORS[req.status])}>
                          <span className={cn("w-1.5 h-1.5 rounded-full",
                            req.status === 'approved' ? 'bg-blue-500' :
                            req.status === 'booked' ? 'bg-indigo-500' :
                            req.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                          )} />
                          {req.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link to={`/app/travel-requests/${req.id}`}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" /> Details
                          </Link>
                          {isRole('manager', 'admin') && req.status === 'pending_approval' && (
                            <>
                              <button
                                onClick={() => handleApprove(req.id)}
                                disabled={!!actionLoading[req.id]}
                                className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                                title="Approve"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setRejectModal({ open: true, id: req.id, comment: '' })}
                                disabled={!!actionLoading[req.id]}
                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={open => setRejectModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Reject Travel Request</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Please state the reason for rejecting this travel request (at least 5 characters).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Rejection Reason *</label>
              <textarea
                rows={3}
                value={rejectModal.comment}
                onChange={e => setRejectModal(p => ({ ...p, comment: e.target.value }))}
                placeholder="Reason for rejection..."
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setRejectModal(p => ({ ...p, open: false }))}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRejectSubmit}
              disabled={!!actionLoading[rejectModal.id] || !rejectModal.comment.trim() || rejectModal.comment.trim().length < 5}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
            >
              Confirm Rejection
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
